import {
  Component,
  forwardRef,
  input,
  signal,
  computed,
  inject,
  afterNextRender,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TextsService } from '@app/services/texts.service';

@Component({
  standalone: false,
  selector: 'webform-range',
  templateUrl: './range.component.html',
  styleUrl: './range.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RangeComponent),
      multi: true,
    },
  ],
})
export class RangeComponent implements ControlValueAccessor {
  readonly isRating = input(false);
  readonly step = input(1);
  readonly min = input(0);
  readonly max = input(100);
  readonly valuePosition = input<'above' | 'below' | 'left' | 'right'>('below');

  value = signal<number>(0);
  hovered = signal(-1);

  valueTexts = signal<any>({});
  valueText = computed(() => {
    const valIndex = this.range().indexOf(this.value());
    const hovIndex = this.range().indexOf(this.hovered());
    return this.valueTexts()[hovIndex] || this.valueTexts()[valIndex] || this.value();
  });

  range = computed(() =>
    Array.from({ length: this.max() - this.min() + 1 }, (_, i) => this.min() + i)
  );

  #texts = inject(TextsService);

  afterRender = afterNextRender({
    read: () => {
      if (!this.isRating()) return;

      const promises: any[] = [];
      const txt: any = {};

      this.range().forEach((item: any) => {
        promises.push(this.#texts.getText(`rating_text_${item}`, 'frontend', item));
      });

      Promise.all(promises).then((texts) => {
        for (let i = 0; i < texts.length; i++) {
          txt[i] = texts[i] || i;
        }
        this.valueTexts.set(txt);
      });
    },
  });

  get before() {
    return ['above', 'left'].includes(this.valuePosition());
  }

  get after() {
    return ['below', 'right'].includes(this.valuePosition());
  }

  get vertical() {
    return ['above', 'below'].includes(this.valuePosition());
  }

  // ControlValueAccessor functions
  #onChange = (value: number) => {};
  #onTouched = () => {};

  writeValue(value: number): void {
    this.value.set(value ?? 0);
  }

  registerOnChange(fn: (value: number) => void): void {
    this.#onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.#onTouched = fn;
  }

  onUserChange(value: number) {
    this.value.set(value);
    this.#onChange(value);
    this.#onTouched();
  }

  getInputValueAsNumber(event: Event): number {
    return (event.target as HTMLInputElement).valueAsNumber;
  }
}
